import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { FieldHelper } from '@/components/PatientData/FieldHelper';
import { PatientStepFooter } from '@/components/PatientData/PatientStepFooter';
import { PatientPageLayout } from '@/components/PatientPageLayout';
import { ResponseTextField } from '@/components/ResponseTextField';
import { useInjectProfession } from '@/hooks/useInjectProfession';
import { usePatientDataCollectionConfig } from '@/hooks/usePatientDataCollectionConfig';
import { useSavePatientStep } from '@/hooks/useSavePatientStep';
import { RootState } from '@/types/graph';
import { scrollToTop } from '@/util/scroll';

// The form initialises from the stored patient once, so remount it when the
// patient changes rather than re-syncing its state.
export const PatientProfession = () => {
  const patientId = useSelector(
    (state: RootState) => state.consultation.patient?.id,
  );
  return <PatientProfessionForm key={patientId} />;
};

const PatientProfessionForm = () => {
  const { patient, save, isSaving, submitError, clearSubmitError } =
    useSavePatientStep('PATIENT_PROFESSION');
  const interlocuter = useSelector(
    (state: RootState) => state.questionnaire.interlocuter,
  );
  const config = usePatientDataCollectionConfig();
  const injectProfession = useInjectProfession();

  const [profession, setProfession] = useState(patient?.profession ?? '');
  const [error, setError] = useState<string>();

  useEffect(() => {
    scrollToTop();
  }, []);

  const isRequired = config.PROFESSION.required;

  const handleSubmit = () => {
    clearSubmitError();
    const value = profession.trim();
    if (!value && isRequired) {
      setError('Ce champ est obligatoire');
      return;
    }

    save(
      { profession: value || null },
      {
        onSaved: (savedPatient) => injectProfession(savedPatient.profession),
        onRejectedFields: (fields) => {
          if (!fields.includes('profession')) return false;
          setError('Profession invalide (100 caractères maximum)');
          return true;
        },
      },
    );
  };

  return (
    <PatientPageLayout
      columnClassName="w-full"
      title={
        <span className="flex flex-col">
          <p className="text-2xl min-[23rem]:text-3.5xl font-semibold nxs:text-center">
            {interlocuter === 'OTHER'
              ? 'Quelle est la profession du patient ?'
              : 'Quelle est votre profession ?'}
          </p>
          <p className="nxs:text-center font-light text-sm mt-2">
            Certaines professions exposent à des
            <br className="nxs:hidden" /> risques utiles à connaître
          </p>
        </span>
      }
      footer={
        <PatientStepFooter
          onSubmit={handleSubmit}
          isSaving={isSaving}
          submitError={submitError}
          disabled={!!error}
        />
      }
    >
      <div className="flex flex-col w-full items-center gap-3 py-4 z-10">
        <div className="flex flex-col w-full gap-1 max-w-86">
          <ResponseTextField
            label={
              isRequired ? 'Ex. : enseignant' : 'Ex. : enseignant (facultatif)'
            }
            value={profession}
            onChange={(value) => {
              setProfession(value || '');
              setError(undefined);
            }}
            error={!!error}
          />
          <FieldHelper error>{error}</FieldHelper>
        </div>
      </div>
    </PatientPageLayout>
  );
};
